import { FC, useRef, useState } from 'react';
import { ReactComponent as CopyIcon } from 'assets/icons/copy.svg';
import { Popover } from '@headlessui/react';
import { createPortal } from 'react-dom';
import ReactTooltip from 'react-tooltip';
import { IService } from 'interfaces/services';
import CommonButton from 'components/CommonButton/CommonButton';
import { SERVICE_NAME } from 'utils/constants';
import { orderBy } from 'lodash';
import ConfirmationModal from 'components/ConfirmationModal/ConfirmationModal';
import { TimeFrame } from '../AdvancedSetting';
import axiosInstance from 'apis/axiosInstance';
import { renderToast } from 'components/Toast/Toast';
import useMatchMutate from 'hooks/useMatchMutate';

interface ExpandHeaderProps {
  services: IService[];
  currentService: IService;
  timeFrames: TimeFrame[];
  isDataValid: boolean;
  syncedWorkingHourLabelId: string | null;
}

const ExpandHeader: FC<ExpandHeaderProps> = ({
  services,
  currentService,
  timeFrames,
  isDataValid,
  syncedWorkingHourLabelId,
}) => {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const triggerButtonRef = useRef<HTMLButtonElement>(null);
  const matchMutate = useMatchMutate();

  const [isModalOpen, setIsModalOpen] = useState(false);

  const childExamCleaning =
    services.find((service) => service.name === SERVICE_NAME.EXAM_CLEANING)
      ?.childServices ?? [];

  const mapServices = [...services, ...childExamCleaning].filter(
    (service) => service.name !== SERVICE_NAME.EXAM_CLEANING
  );

  const servicesWithoutCurrentService = mapServices.filter(
    (service) => service.id !== currentService.id && service.isActive
  );

  const [servicesData, setServicesData] = useState(() =>
    orderBy(
      servicesWithoutCurrentService.map((service) => ({
        id: service.id,
        name: service.name,
        checked: false,
        isExamCleaning: service.name.includes(SERVICE_NAME.EXAM_CLEANING),
      })),
      ['isExamCleaning', 'name'],
      ['desc', 'asc']
    )
  );

  const onChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    serviceId: string
  ) => {
    e.stopPropagation();
    const { checked } = e.target;
    const updatedServicesData = servicesData.map((service) =>
      service.id === serviceId ? { ...service, checked } : service
    );
    setServicesData(updatedServicesData);
  };

  const onSubmit = async () => {
    const payload = timeFrames.map((timeFrame) => {
      const { day, timeSlots } = timeFrame;
      const isAvailable = timeSlots.some(
        (timeSlot) => timeSlot.start && timeSlot.end
      );
      const shifts = timeSlots.map((timeSlot) => ({
        startTime: timeSlot.start,
        endTime: timeSlot.end,
        operatoryIds: timeSlot.operatories.map((operatory) => operatory.value),
      }));

      return {
        day,
        isAvailable,
        shifts: isAvailable
          ? shifts
          : [{ startTime: null, endTime: null, operatoryIds: [] }],
      };
    });
    const listOfSelectedServices = servicesData.filter(
      (service) => service.checked
    );

    const timeFramsPromises = listOfSelectedServices.map((service) =>
      axiosInstance.post(`/services/${service.id}/schedules`, payload)
    );

    const blockoutTypePromises = axiosInstance.patch(
      `block-scheduling`,
      listOfSelectedServices.map((service) => ({
        serviceId: service.id,
        syncedWorkingHourLabelId: syncedWorkingHourLabelId ?? null,
      }))
    );

    try {
      await Promise.all([...timeFramsPromises, blockoutTypePromises]);
      await matchMutate(/\/services\?[\s\S]+/);

      setIsModalOpen(false);
      renderToast({
        message: 'Blockout type and time frames updated successfully',
        type: 'success',
      });
      setServicesData((prev) =>
        prev.map((service) => ({ ...service, checked: false }))
      );
    } catch (error) {
      renderToast({
        message: 'Something went wrong. Please try again later.',
        type: 'error',
      });
    }
  };

  const onAppliedButtonClicked = () => {
    if (isDataValid) {
      return setIsModalOpen(true);
    }

    renderToast({
      message:
        'Please ensure all time frames have valid data before applying to other services',
      type: 'error',
    });
  };

  return (
    <>
      {isModalOpen && (
        <ConfirmationModal
          title={'Update blockout type and time frames for these services?'}
          description={
            'This action will update the blockout type and time frames of the selected services to be the same as this service. Are you sure you want to continue?'
          }
          onClose={() => {
            setIsModalOpen(false);
          }}
          onSubmit={onSubmit}
          submitBtnTitle="Yes, apply"
        />
      )}
      <div className="flex w-full flex-1 items-center justify-between">
        <span className="text-16 leading-[2.4rem] font-bold">
          Copy Settings
        </span>
        <Popover className="relative">
          {({ open }) => (
            <>
              <button
                className={`p-0.5 flex justify-center items-center hover:bg-[#F5F0F5] focus:bg-[#F5F0F5] outline-none ${
                  open ? 'bg-[#F5F0F5]' : ''
                }`}
                data-for={`copy-setting`}
                data-tip=""
                onClick={() => {
                  buttonRef.current?.click();
                }}
                ref={triggerButtonRef}
              >
                <CopyIcon className="fill-darkest-grey w-1.6 h-1.6" />
              </button>
              <Popover.Button as="button" className="hidden" ref={buttonRef} />
              <Popover.Panel
                className={`w-30 right-0 absolute mt-1.1 bg-white rounded-[1rem] shadow-primary text-darkest-grey text-12 leading-[1.8rem] z-[9999]`}
                static={isModalOpen || open}
              >
                <div className="pt-1.6 pb-1.8 px-2">
                  <h3>Copy current settings to </h3>
                  <div className="max-h-[40rem] overflow-auto flex flex-col justify-between gap-y-1.6 scrollbar py-2 -mr-1 pr-1">
                    {servicesData.map((service) => (
                      <div
                        className="flex justify-between items-center"
                        key={service.id}
                      >
                        <label
                          className="text-left"
                          htmlFor={`option-${service.id}`}
                        >
                          {service.name}
                        </label>
                        <input
                          id={`option-${service.id}`}
                          type="checkbox"
                          className="w-1.8 h-1.8"
                          checked={service.checked}
                          onChange={(e) => onChange(e, service.id)}
                        />
                      </div>
                    ))}
                  </div>

                  <CommonButton
                    className="!min-w-[11rem] w-full mt-1"
                    disabled={!servicesData.some((service) => service.checked)}
                    onClick={onAppliedButtonClicked}
                  >
                    Apply
                  </CommonButton>
                </div>
              </Popover.Panel>

              {createPortal(
                <ReactTooltip
                  id={`copy-setting`}
                  effect="solid"
                  className="!bg-lightest-grey !border-light-grey !rounded-[0.8rem] !min-w-[14rem] !h-[2.6rem] !px-[1rem] !py-[0.4rem] !opacity-100 z-[9999] flex justify-center items-center"
                >
                  <span className="text-11 text-darkest-grey flex items-center font-normal">
                    Copy blockout type and time frames to other services
                  </span>
                </ReactTooltip>,
                document.body
              )}
            </>
          )}
        </Popover>
      </div>
    </>
  );
};

export default ExpandHeader;
